import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { tapFeedback } from "../feedback";

// Served from public/intro/ and never precached (vite.config.ts): the film is several MB and
// streams on demand.
const VIDEO = "/intro/pointz-intro.mp4";
export const INTRO_POSTER = "/intro/poster.jpg";

/**
 * The intro film, full screen on black and letterboxed to any phone. It never starts by itself:
 * iOS plays sound only after a tap, so the poster waits under a big Play button. Skip is there
 * from the first frame; the end, Skip and Escape all close it, and a tap on the film pauses or
 * resumes it. `onClose` hears whether it was watched to the end. Signed out (on Login) there is
 * no session to record the watch against, so `signedIn={false}` skips that call.
 */
export function IntroPlayer({
  onClose,
  signedIn = true,
}: {
  onClose: (completed: boolean) => void;
  signedIn?: boolean;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const play = useRef<HTMLButtonElement>(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  // A close counts once, however many of the end, Skip and Escape arrive. Kept in a ref so the
  // Escape listener, added once, always reaches the latest onClose.
  const closed = useRef(false);
  const close = useRef<(completed: boolean) => void>(() => undefined);
  close.current = (completed: boolean) => {
    if (closed.current) return;
    closed.current = true;
    const seconds = Math.round(video.current?.currentTime ?? 0);
    if (signedIn) api.introWatched(completed, seconds).catch(() => undefined);
    onClose(completed);
  };

  useEffect(() => {
    tapFeedback(25);
    // Focus goes back where it was (Settings' button) once the film closes.
    const before = document.activeElement as HTMLElement | null;
    play.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close.current(false);
    };
    document.addEventListener("keydown", onKey);
    // The page underneath must not scroll behind the film.
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      root.style.overflow = overflow;
      before?.focus();
    };
  }, []);

  function toggle() {
    const v = video.current;
    if (!v || failed) return;
    if (v.paused) {
      // Refused (no gesture, nothing playable): the poster and Play stay put.
      v.play().catch(() => setPlaying(false));
    } else {
      v.pause();
    }
  }

  return (
    <div className="intro" role="dialog" aria-modal="true" aria-label="Pointz intro">
      <video
        ref={video}
        className="intro__video"
        src={VIDEO}
        poster={INTRO_POSTER}
        playsInline
        preload="metadata"
        aria-label="The Pointz intro film"
        onClick={toggle}
        onPlay={() => {
          setStarted(true);
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => close.current(true)}
        onError={() => setFailed(true)}
      />
      {failed ? (
        <p className="intro__note">The intro can't play right now.</p>
      ) : (
        !playing && (
          <button ref={play} className="intro__play" type="button" onClick={toggle}>
            <span aria-hidden="true">{"▶︎"}</span> {started ? "Keep watching" : "Play the intro"}
          </button>
        )
      )}
      <button className="intro__skip" type="button" onClick={() => close.current(false)}>
        {failed ? "Close" : "Skip"}
      </button>
    </div>
  );
}
